import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../utils/components/ui/dialog";
import { Button } from "../../utils/components/ui/button";
import { Input } from "../../utils/components/ui/input";
import { VendorInvoice } from "../../types/vendorManagement";
import {
  useVendorSettlement,
  type VendorSettlementRequest,
} from "../../service/vendorService";
import { toast } from "react-toastify";

type PaymentMode = VendorSettlementRequest["paymentMode"];

const PAYMENT_MODES: PaymentMode[] = ["CASH", "CARD", "ONLINE_PAYMENT", "CHEQUE"];

type PayVendorBalanceDialogProps = {
  invoice: VendorInvoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

const formatCurrency = (amount: number) =>
  `OMR ${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(amount)}`;

function PayVendorBalanceDialog({
  invoice,
  open,
  onOpenChange,
  onSuccess,
}: PayVendorBalanceDialogProps) {
  const [amountToPay, setAmountToPay] = useState("");
  const [paidOn, setPaidOn] = useState("");
  const [paymentMode, setPaymentMode] = useState<PaymentMode>("CASH");
  const [chqNumber, setChqNumber] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [onlineRef, setOnlineRef] = useState("");

  const { mutate: settle, isPending } = useVendorSettlement();

  useEffect(() => {
    if (invoice) {
      setAmountToPay(String(invoice.balanceAmount || ""));
      setPaidOn(new Date().toISOString().slice(0, 10));
      setPaymentMode("CASH");
      setChqNumber("");
      setCardNumber("");
      setOnlineRef("");
    }
  }, [invoice]);

  const handleClose = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setAmountToPay("");
      setPaidOn("");
    }
  };

  const handleSubmit = () => {
    if (!invoice) return;

    settle(
      {
        purchaseEntryId: Number(invoice.id),
        amountPaid: Number(amountToPay),
        paymentDate: paidOn,
        paymentMode,
        chqNumber: chqNumber || undefined,
        cardNumber: cardNumber || undefined,
        onlinePaymentRefNo: onlineRef || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Payment submitted successfully");
          handleClose(false);
          onSuccess();
        },
        onError: (error: Error) => {
          toast.error(`Failed to submit payment: ${error.message}`);
        },
      },
    );
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[740px] rounded-[24px] border-none p-0 shadow-[0_24px_60px_rgba(0,0,0,0.16)] sm:max-w-[740px]">
        <div className="p-8 sm:p-10">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-[44px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
              Pay Balance
            </DialogTitle>
          </DialogHeader>

          {/* Invoice summary */}
          <div className="grid gap-4 border-b border-[#E8EEF9] pb-8 md:grid-cols-4">
            <div>
              <p className="mb-2 text-[15px] text-[#8A948D]">Invoice ID</p>
              <div className="rounded-[10px] bg-[#F4F7FC] px-4 py-3 text-[15px] font-semibold text-[#415043]">
                {invoice.invoiceId}
              </div>
            </div>
            <div>
              <p className="mb-2 text-[15px] text-[#8A948D]">Total Amount</p>
              <div className="rounded-[10px] bg-[#F4F7FC] px-4 py-3 text-[15px] font-semibold text-[#415043]">
                {formatCurrency(invoice.amount)}
              </div>
            </div>
            <div>
              <p className="mb-2 text-[15px] text-[#8A948D]">Amount Paid</p>
              <div className="rounded-[10px] bg-[#F4F7FC] px-4 py-3 text-[15px] font-semibold text-[#415043]">
                {formatCurrency(invoice.amountPaid)}
              </div>
            </div>
            <div>
              <p className="mb-2 text-[15px] text-[#8A948D]">Balance Amount</p>
              <div className="rounded-[10px] bg-[#F4F7FC] px-4 py-3 text-[15px] font-semibold text-[#415043]">
                {formatCurrency(invoice.balanceAmount)}
              </div>
            </div>
          </div>

          {/* Payment fields */}
          <div className="space-y-5 py-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-[15px] text-[#8A948D]">
                  Amount to Pay
                </label>
                <Input
                  type="number"
                  value={amountToPay}
                  onChange={(e) => setAmountToPay(e.target.value)}
                  placeholder="Enter amount"
                  className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base font-semibold shadow-none"
                />
              </div>
              <div>
                <label className="mb-2 block text-[15px] text-[#8A948D]">
                  Payment Date
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={paidOn}
                    onChange={(e) => setPaidOn(e.target.value)}
                    className="h-12 rounded-[10px] border-none bg-[#F4F7FC] pr-12 text-base font-semibold shadow-none"
                  />
                  <CalendarDays className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-[#758274]" />
                </div>
              </div>
            </div>

            {/* Payment mode tabs */}
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Payment Mode
              </label>
              <div className="flex gap-2 flex-wrap">
                {PAYMENT_MODES.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setPaymentMode(mode)}
                    className={`rounded-[10px] px-5 py-2 text-sm font-semibold transition-colors ${
                      paymentMode === mode
                        ? "bg-[#1A5EF9] text-white"
                        : "bg-[#F4F7FC] text-[#415043] hover:bg-[#E8EEF9]"
                    }`}
                  >
                    {mode === "ONLINE_PAYMENT" ? "ONLINE" : mode}
                  </button>
                ))}
              </div>
            </div>

            {/* Conditional reference fields */}
            {paymentMode === "CHEQUE" && (
              <div>
                <label className="mb-2 block text-[15px] text-[#8A948D]">
                  Cheque Number
                </label>
                <Input
                  value={chqNumber}
                  onChange={(e) => setChqNumber(e.target.value)}
                  placeholder="Enter cheque number"
                  className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base font-semibold shadow-none"
                />
              </div>
            )}
            {paymentMode === "CARD" && (
              <div>
                <label className="mb-2 block text-[15px] text-[#8A948D]">
                  Card Number
                </label>
                <Input
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder="Enter card number"
                  className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base font-semibold shadow-none"
                />
              </div>
            )}
            {paymentMode === "ONLINE_PAYMENT" && (
              <div>
                <label className="mb-2 block text-[15px] text-[#8A948D]">
                  Online Payment Ref No.
                </label>
                <Input
                  value={onlineRef}
                  onChange={(e) => setOnlineRef(e.target.value)}
                  placeholder="Enter reference number"
                  className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base font-semibold shadow-none"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-[#E8EEF9] pt-8 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="h-12 rounded-[10px] border-[#D6DDD0] px-8 text-[#7E8A80]"
              onClick={() => handleClose(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              className="h-12 rounded-[10px] bg-[#1A5EF9] px-8 text-white hover:bg-[#0E4FDE]"
              onClick={handleSubmit}
              disabled={!amountToPay || !paidOn || isPending}
            >
              {isPending ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PayVendorBalanceDialog;
